<script setup lang="ts">
  import { computed, ref, watch, onBeforeUnmount, useSlots } from "vue";
  import { useEditor, EditorContent } from "@tiptap/vue-3";
  import { BubbleMenu } from "@tiptap/vue-3/menus";
  import StarterKit from "@tiptap/starter-kit";
  import Placeholder from "@tiptap/extension-placeholder";
  import Link from "@tiptap/extension-link";
  import type { AnyExtension } from "@tiptap/core";
  import { Plugin, PluginKey } from "@tiptap/pm/state";
  import type { MarkType } from "@tiptap/pm/model";
  import { Marked } from "marked";
  import { lenientEmphasis } from "@utils/markedExtensions";
  import FloatingToolbar from "./FloatingToolbar.vue";
  import RichInputEmojiPicker from "./RichInputEmojiPicker.vue";
  import { createMentionExtension } from "./mention-extension";
  import { extractTextFromHtml } from "./richInputText";
  import type { MentionItem } from "./mention-extension";

  // Dedicated Marked instance: handles bold/italic/explicit links
  // but does NOT auto-link bare URLs (disables GFM url + autolink tokenizers)
  const richInputMarked = new Marked();
  richInputMarked.use(lenientEmphasis());
  richInputMarked.use({
    tokenizer: {
      url() {
        return undefined;
      },
      autolink() {
        return undefined;
      },
    },
  });

  const props = withDefaults(
    defineProps<{
      value?: string;
      placeholder?: string;
      disabled?: boolean;
      readonly?: boolean;
      size?: "default" | "lg";
      type?: "input" | "textarea";
      mentionOptions?: MentionItem[];
      showEmoji?: boolean;
      controlsActive?: boolean;
      autofocus?: boolean;
      rows?: number;
      plainText?: boolean;
    }>(),
    {
      value: "",
      placeholder: "",
      disabled: false,
      readonly: false,
      size: "default",
      type: "input",
      mentionOptions: () => [],
      showEmoji: true,
      controlsActive: false,
      autofocus: false,
      rows: 2,
      plainText: false,
    },
  );

  const slots = useSlots();
  const isEditable = computed(() => !props.disabled && !props.readonly);

  const emit = defineEmits<{
    "update-input": [value: string];
  }>();

  const isFocused = ref(false);
  const emojiPickerOpen = defineModel<boolean>("emojiOpen", { default: false });
  const extensions = computed((): AnyExtension[] => {
    const baseExtensions: AnyExtension[] = [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        hardBreak: props.type === "textarea" ? {} : false,
        // No text formatting in plain-text mode (ad copy / campaign fields).
        ...(props.plainText
          ? { bold: false, italic: false, strike: false, code: false }
          : {}),
      }),
      Placeholder.configure({
        placeholder: props.placeholder,
      }),
    ];

    if (!props.plainText) {
      baseExtensions.push(
        Link.extend({
          addPasteRules() {
            return [];
          },
          addProseMirrorPlugins() {
            const linkType: MarkType = this.type;
            // Single plugin: actively strip link marks where text === href (auto-links).
            // Intentional links (text !== href, created via toolbar) are preserved.
            return [
              new Plugin({
                key: new PluginKey("stripAutoLinks"),
                appendTransaction: (_transactions, _oldState, newState) => {
                  const { tr } = newState;
                  let modified = false;
                  newState.doc.descendants((node, pos) => {
                    if (!node.isText) return;
                    const linkMark = node.marks.find(
                      (mark) => mark.type === linkType,
                    );
                    if (!linkMark) return;
                    const href = (linkMark.attrs.href as string) || "";
                    const text = node.text || "";
                    if (
                      text === href ||
                      text === href.replace(/\/$/, "") ||
                      href === text.replace(/\/$/, "")
                    ) {
                      tr.removeMark(pos, pos + node.nodeSize, linkType);
                      modified = true;
                    }
                  });
                  return modified ? tr : null;
                },
              }),
            ];
          },
        }).configure({
          HTMLAttributes: {
            rel: "noopener noreferrer",
            target: "_blank",
          },
        }),
      );
    }

    if (props.mentionOptions.length) {
      baseExtensions.push(createMentionExtension(props.mentionOptions));
    }

    return baseExtensions;
  });

  const escapeHtml = (str: string): string =>
    str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const convertTextToHtml = (text: string): string => {
    if (!text) return "<p></p>";

    // Replace mentions with placeholder spans before parsing
    // so markdown / escaping don't interfere with @mention syntax
    const mentionPlaceholders: string[] = [];
    let html = text.replace(/@(\w+\([^)]*\)|loop_\w+)/g, (_match, id) => {
      const placeholder = `\x00MENTION${mentionPlaceholders.length}\x00`;
      mentionPlaceholders.push(`<span class="mention" data-id="${id}">@${id}</span>`);
      return placeholder;
    });

    // In plain-text mode keep the text literal (no bold/italic); otherwise use
    // marked.parseInline for bold/italic (lenient via extension).
    const lines = html.split("\n");
    return lines
      .map((line) => {
        let parsed = props.plainText
          ? escapeHtml(line)
          : (richInputMarked.parseInline(line) as string);
        // Restore mention placeholders
        mentionPlaceholders.forEach((span, i) => {
          parsed = parsed.replace(`\x00MENTION${i}\x00`, span);
        });
        return `<p>${parsed}</p>`;
      })
      .join("");
  };

  const editor = useEditor({
    content: convertTextToHtml(props.value),
    extensions: extensions.value,
    editable: isEditable.value,
    editorProps: {
      // Force plain-text paste to prevent clipboard HTML from creating links.
      // Links should only be added explicitly via the floating toolbar.
      handlePaste(view, event) {
        const text = event.clipboardData?.getData("text/plain");
        if (!text) return false;
        view.dispatch(view.state.tr.insertText(text));
        return true;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = extractTextFromHtml(html);
      emit("update-input", text);
    },
    onFocus: () => (isFocused.value = true),
    onBlur: () => (isFocused.value = false),
    onCreate: ({ editor }) => {
      if (props.autofocus && isEditable.value) editor.commands.focus("end");
    },
  });

  watch(
    () => props.value,
    (newValue) => {
      if (!editor.value) return;

      const currentText = extractTextFromHtml(editor.value.getHTML());
      if (currentText !== newValue) {
        editor.value.commands.setContent(convertTextToHtml(newValue || ""));
      }
    },
  );

  watch(isEditable, (editable) => {
    if (editor.value) {
      editor.value.setEditable(editable);
    }
  });

  watch(
    () => props.placeholder,
    () => {
      if (editor.value) {
        editor.value.setOptions({ extensions: extensions.value });
      }
    },
    { immediate: true },
  );

  const insertEmoji = (emoji: string): void => {
    if (editor.value) {
      editor.value.chain().focus().insertContent(emoji).run();
    }
  };

  const getMinHeight = computed(() => {
    if (props.type === "input") return "auto";
    // Each row is approximately 1.5rem (24px) of line height
    const lineHeight = 1.5;
    const heightInRem = props.rows * lineHeight;
    return `${heightInRem}rem`;
  });

  const wrapperClasses = computed(() => [
    "relative w-full rounded-md border border-input bg-background px-3 py-1.5",
    props.size === "lg" ? "text-13 font-medium text-primary" : "text-13",
    isEditable.value && "cursor-text",
    "ring-offset-background",
    "focus-within:outline-none focus-within:ring-1 focus-within:ring-inset focus-within:ring-ring",
    props.disabled && "opacity-50 cursor-not-allowed",
    slots.append && props.showEmoji ? "pr-16" : slots.append || props.showEmoji ? "pr-8" : "",
    props.type === "input" && "flex items-center",
  ]);

  const focusFromFrame = (event: MouseEvent) => {
    if (!isEditable.value || !editor.value) return;
    if ((event.target as HTMLElement).closest(".ProseMirror, button, [role=button]")) return;
    event.preventDefault();
    editor.value.commands.focus("end");
  };

  onBeforeUnmount(() => {
    editor.value?.destroy();
  });
</script>

<template>
  <div
    data-slot="rich-input"
    :class="wrapperClasses"
    @mousedown="focusFromFrame"
>
    <EditorContent
      :editor="editor"
      :style="{ minHeight: getMinHeight }"
      class="w-full [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[inherit] [&_.ProseMirror_p]:m-0 [&_.ProseMirror_p.is-empty:first-child]:before:text-muted-foreground [&_.ProseMirror_p.is-empty:first-child]:before:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-empty:first-child]:before:float-left [&_.ProseMirror_p.is-empty:first-child]:before:h-0 [&_.ProseMirror_p.is-empty:first-child]:before:pointer-events-none [&_.ProseMirror_p.is-empty:first-child]:after:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-empty:first-child]:after:block [&_.ProseMirror_p.is-empty:first-child]:after:invisible [&_.mention]:bg-primary/10 [&_.mention]:text-primary [&_.mention]:rounded [&_.mention]:px-1 [&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:underline-offset-2 [&_.ProseMirror_a]:pointer-events-none" />

    <BubbleMenu
      v-if="editor && !plainText"
      :editor="editor"
      :tippy-options="{ duration: 100 }">
      <FloatingToolbar :editor="editor" />
    </BubbleMenu>

    <div
      v-if="$slots.append || showEmoji"
      class="absolute right-1.5 z-10 flex items-center gap-1"
      :class="props.type === 'input' ? 'top-1/2 -translate-y-1/2' : 'top-1.5'">
      <RichInputEmojiPicker
        v-if="showEmoji && (isFocused || emojiPickerOpen)"
        v-model:open="emojiPickerOpen"
        @select="insertEmoji" />
      <slot name="append" />
    </div>
  </div>
</template>
